import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Children, Fragment, isValidElement, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import { completePartialMarkdown } from './complete-partial-markdown';

import { CopyButton } from '@/components/ui/copy-button';
import { cn } from '@/lib/utils';

interface ResponseProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Builds a TSV string by walking the table's own rows/cells — the plain-text
 * clipboard flavour for a spreadsheet paste, since a table's next stop is
 * almost always a spreadsheet.
 */
function tableToTsv(table: HTMLTableElement): string {
  const rows = Array.from(table.querySelectorAll('tr'));
  return rows
    .map((row) =>
      Array.from(row.querySelectorAll('th, td'))
        .map((cell) => (cell.textContent ?? '').trim())
        .join('\t'),
    )
    .join('\n');
}

/**
 * A rendered markdown table, with a copy affordance.
 *
 * `react-markdown`'s `components` map is a plain object of render callbacks,
 * not components with their own render scope in the way a hook needs — so
 * this is pulled out as its own component to legally own a `useRef` for the
 * table DOM node, rather than reaching for the DOM some other way from
 * inside the map.
 *
 * Reference: `.claude/skills/respire-design-system/references/chat-surfaces.md`
 * §2 — "a copy affordance at the top-right."
 */
function MarkdownTable(props: React.TableHTMLAttributes<HTMLTableElement>) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  // A wide table (more columns than a mobile/panel width can show) already
  // scrolled horizontally via `overflow-x-auto` below — the mechanism
  // worked. What was missing, measured live 2026-09-06 at 390px inside a
  // generated document: any VISIBLE sign of it. Columns past the fold were
  // simply gone with no scrollbar, no fade, no hint — "silently cut" was
  // an accurate description of a table that technically scrolled but gave
  // no reason to try. Same caret-badge affordance as the composer's mode
  // pills (`ComposerModePills.tsx`), computed from real scroll geometry
  // rather than a breakpoint.
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 1);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, [props.children]);

  return (
    <div ref={wrapperRef} className="group relative my-4 overflow-x-auto">
      <table {...props} className="w-full border-collapse text-sm" />
      {/*
       * Pinned near the header row (`top-9`, clearing the CopyButton's own
       * `top-0` row), NOT vertically centered — `inset-y-0 items-center`
       * was tried first and centered on the WHOLE table's height, which for
       * a many-row table is taller than the viewport, landing the badge
       * hundreds of pixels below the fold on first paint (measured: a
       * 6-row table stood 1110px tall). Anchoring near the top instead
       * keeps it where the table actually comes into view. It scrolls away
       * with the table content once the user starts scrolling — that's
       * fine, its only job is to say "there's more" before that first
       * scroll. Same affordance and reasoning as the composer's mode pills
       * (`ComposerModePills.tsx`), computed from real scroll geometry
       * rather than a breakpoint.
       */}
      {canScrollLeft && (
        <div aria-hidden="true" className="pointer-events-none absolute left-0.5 top-9">
          <div className="flex size-5 items-center justify-center rounded-full border border-border/70 bg-background text-muted-foreground/80 shadow-sm">
            <CaretLeftIcon className="size-3" weight="bold" />
          </div>
        </div>
      )}
      {canScrollRight && (
        <div aria-hidden="true" className="pointer-events-none absolute right-0.5 top-9">
          <div className="flex size-5 items-center justify-center rounded-full border border-border/70 bg-background text-muted-foreground/80 shadow-sm">
            <CaretRightIcon className="size-3" weight="bold" />
          </div>
        </div>
      )}
      <CopyButton
        /*
         * `top-0`, not `-top-2`: `overflow-x-auto` computes `overflow-y: auto`
         * too, which makes this wrapper a clipping box — a negative offset puts
         * the glyph's top edge outside it and gets it cut off. It doesn't need
         * the offset anyway: `overflow` also establishes a BFC, so the table's
         * own prose top margin is CONTAINED by the wrapper, leaving a gap at
         * `top-0` that the glyph sits in cleanly without covering the header.
         *
         * No background chip. A filled rectangle materialising under a 14px
         * glyph is a second object arriving on hover, louder than the thing it
         * is highlighting — the rule already settled on the sidebar section
         * headers. Hover is opacity only.
         */
        className="absolute right-0 top-0"
        label="Copy table"
        successMessage="Table copied"
        getText={() => {
          const table = wrapperRef.current?.querySelector('table');
          return table ? tableToTsv(table) : '';
        }}
        getHtml={() => wrapperRef.current?.querySelector('table')?.outerHTML ?? ''}
      />
    </div>
  );
}

/**
 * `CitationParser` hands `Response` an ARRAY of children (string runs
 * interleaved with `<InlineCitation>` elements) whenever an answer has
 * citations. The array branch below used to render that array with no
 * markdown pass at all, so a cited answer containing `**bold**` or a table
 * showed the literal markdown syntax to the user — confirmed against
 * production, 3 of 1,098 stored answers hit this, all 3 containing markdown.
 *
 * This still special-cases a pure-string array (the common case: no
 * citations) by joining it back into one string and taking the normal
 * markdown path. A genuinely mixed array (citations present) renders each
 * string run through markdown and keeps the non-string element in place
 * between runs, so citations stay inline and the surrounding prose still
 * formats.
 */
function childrenToMarkdownRuns(
  children: React.ReactNode,
): { text: string; element: React.ReactNode }[] {
  const runs: { text: string; element: React.ReactNode }[] = [];
  let buffer = '';

  for (const child of Children.toArray(children)) {
    if (typeof child === 'string' || typeof child === 'number') {
      buffer += String(child);
    } else if (isValidElement(child)) {
      runs.push({ text: buffer, element: null });
      buffer = '';
      runs.push({ text: '', element: child });
    }
  }
  runs.push({ text: buffer, element: null });

  return runs;
}

/**
 * An INTERNAL link must route, not open a tab.
 *
 * Every anchor used to get `target="_blank"`, so when an agent told the user
 * to "connect an ad account in Settings → Integrations" and linked it, the
 * link either did nothing useful or tore them out of the SPA into a second
 * tab that reloaded the whole app. Founder, 2026-08-15: "doesn't even take
 * them to the page to connect it".
 *
 * Same-origin paths go through react-router so the destination opens in
 * place, with the conversation still there when they come back. External
 * links keep the new tab and the `noopener` hardening — that part was right.
 */
function MarkdownLink({
  node: _node,
  href,
  children,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) {
  const isInternal = typeof href === 'string' && /^\/(?!\/)/.test(href);
  if (isInternal) {
    return (
      <Link {...props} to={href as string}>
        {children}
      </Link>
    );
  }
  return (
    <a {...props} href={href} rel="noopener noreferrer" target="_blank">
      {children}
    </a>
  );
}

/**
 * A table is the default form for structured answer content, so it has to
 * read as editorial prose rather than as a spreadsheet.
 *
 * Three things do that, and all three were wrong: cells are flush with the
 * surrounding paragraphs (`first:pl-0`) so the table doesn't sit in its own
 * indented box; rows breathe at `py-3` rather than `py-2`; and body cells
 * stay at reading contrast. Muting every `td` washed the whole table out —
 * the label column already reads quieter through brevity and position, and
 * doesn't need colour to do it.
 *
 * Reference: `.claude/skills/respire-design-system/references/chat-surfaces.md` §2.
 */
const markdownComponents: Record<string, React.ComponentType<Record<string, unknown>>> = {
  a: MarkdownLink,
  table: ({
    node: _node,
    ...props
  }: React.TableHTMLAttributes<HTMLTableElement> & { node?: unknown }) => (
    <MarkdownTable {...props} />
  ),
  thead: ({
    node: _node,
    ...props
  }: React.HTMLAttributes<HTMLTableSectionElement> & { node?: unknown }) => (
    <thead {...props} className="border-b border-border/60 text-left" />
  ),
  tbody: ({
    node: _node,
    ...props
  }: React.HTMLAttributes<HTMLTableSectionElement> & { node?: unknown }) => (
    <tbody {...props} className="divide-y divide-border/40" />
  ),
  th: ({
    node: _node,
    ...props
  }: React.ThHTMLAttributes<HTMLTableCellElement> & { node?: unknown }) => (
    <th
      {...props}
      className="py-3 pr-6 font-medium tabular-nums text-foreground first:pl-0 last:pr-0"
    />
  ),
  td: ({
    node: _node,
    ...props
  }: React.TdHTMLAttributes<HTMLTableCellElement> & { node?: unknown }) => (
    /*
     * `tabular-nums` is not decoration here. The whole reason a marketer asks
     * for a table is to compare a column of figures — CPA, ROAS, spend, reach —
     * and proportional digits make a column of numbers ragged, so the one thing
     * the form exists to do it does badly. `chat-surfaces.md` §8: tabular-nums
     * on every number, without exception. Harmless on text cells.
     *
     * `[&_strong]:font-medium` enforces the same section's typography ceiling.
     * A `**bold**` label column arrives as `<strong>`, which the browser paints
     * at 700 — two full steps above the 500 ceiling this surface holds, and the
     * loudest thing in the answer. The label column is meant to read quiet
     * through brevity and position, not shout.
     */
    <td
      {...props}
      className="py-3 pr-6 align-top tabular-nums text-foreground/90 first:pl-0 last:pr-0 [&_strong]:font-medium"
    />
  ),
};

/**
 * Used only for the citation-array runs path. Each run is a fragment of the
 * SAME paragraph split at citation-marker boundaries (see
 * `CitationParser.tsx`), not a standalone document, so wrapping each
 * fragment's own `<p>` around it would force a hard line break between every
 * citation and its surrounding sentence. Rendering `p` as a plain fragment
 * here keeps the fragments flowing inline while still formatting `**bold**`,
 * links, etc. within each run.
 */
const inlineMarkdownComponents = {
  ...markdownComponents,
  p: ({ children }: React.HTMLAttributes<HTMLParagraphElement>) => <>{children}</>,
};

export function Response({ children, className }: ResponseProps) {
  if (typeof children !== 'string' && !Array.isArray(children)) {
    return (
      <div
        data-slot="assistant-answer"
        className={cn('text-sm leading-relaxed text-foreground/90', className)}
      >
        {children}
      </div>
    );
  }

  const markdownRuns = Array.isArray(children) ? childrenToMarkdownRuns(children) : null;

  return (
    <div
      /**
       * `data-slot` per the repo's shadcn convention, and the hook
       * `scripts/ui-eval` needs to measure the ANSWER rather than approximate
       * it.
       *
       * Without it the streaming-cadence metric could only watch whole-thread
       * text growth — so a status label appearing at 1.5s read as "the answer
       * started at 1.5s" while the answer actually arrived at 33.6s of a 40s
       * turn. That single mislabel nearly had the smoothness axis reported as
       * solved when it had not moved at all.
       */
      data-slot="assistant-answer"
      className={cn(
        'max-w-none text-sm leading-[1.7] text-foreground/90',
        'prose prose-sm prose-neutral dark:prose-invert',
        // `prose-headings:text-foreground` — without it, headings fall through
        // to `prose-neutral`'s OWN colour variables (`--tw-prose-headings` /
        // `--tw-prose-invert-headings`), a third grey family independent of
        // this app's tokens: measured live 2026-09-06, body/strong render our
        // warm `--foreground` (#39372d light) while headings render Tailwind
        // Typography's neutral default (oklch(.205) light, and — worse —
        // pure #fff in dark, brighter than `--foreground` itself). One
        // override unifies headings into the same family as the text around
        // them.
        'prose-headings:text-foreground',
        'prose-headings:mb-3 prose-headings:mt-8 prose-headings:font-medium prose-headings:tracking-tight first:prose-headings:mt-0',
        'prose-h1:text-xl prose-h2:text-lg prose-h3:text-base prose-h4:text-sm',
        // `prose-strong:font-medium` caps `<strong>` at this surface's weight
        // ceiling. Tailwind Typography's own `strong` rule is 600 regardless
        // of theme; measured live, a `**bold**` sub-label inside a paragraph
        // rendered heavier (14px/600) than the h3 ABOVE it (16px/500) —
        // Design System: "font-medium is the ceiling on this surface." Mirrors
        // the table cell's own `[&_strong]:font-medium` override just below,
        // which existed for `td` but not for prose `strong` generally.
        'prose-p:my-4 prose-p:text-foreground prose-strong:text-foreground prose-strong:font-medium',
        'prose-ul:my-4 prose-ol:my-4 prose-li:my-1.5 prose-li:marker:text-muted-foreground',
        'prose-code:rounded prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:text-[0.85em] prose-code:before:content-none prose-code:after:content-none',
        // `prose-pre:text-foreground` pairs with the `bg-muted` override just
        // before it. Tailwind Typography's `pre` sets its OWN text colour
        // (`--tw-prose-pre-code`, a light grey meant for typography's default
        // dark `pre` background) and `pre code` inherits it verbatim — so
        // once the background is overridden to our light-mode `--muted`
        // (~96% lightness), that grey-on-grey text reads as washed-out ghost
        // text. Measured 2026-09-09: light-mode fenced code blocks were
        // nearly illegible app-wide (every `Response` call site) until this
        // was added.
        'prose-pre:rounded-xl prose-pre:border prose-pre:border-border prose-pre:bg-muted prose-pre:text-foreground prose-pre:px-4 prose-pre:py-3',
        // `prose-code:text-[0.85em]` two lines up is sized for INLINE code
        // sitting inside body text — inside a `pre`, the same 0.85em
        // compounds against `pre`'s own smaller base and measured 10.2px
        // live, under the 11px `text-detail` floor. `pre code` has zero
        // specificity under Typography's own `:where()` selectors, so this
        // reliably wins without touching inline code's sizing.
        '[&_pre_code]:text-caption',
        'prose-blockquote:border-l-border prose-blockquote:text-muted-foreground',
        'prose-hr:border-border',
        // 🔴 A LINK MUST LOOK LIKE A LINK. This was
        // `prose-a:text-primary prose-a:no-underline`, and in this theme
        // `--primary` is near-black: measured in the article reader,
        // 2026-09-07, an anchor rendered rgb(37,36,29) against body text at
        // rgb(57,55,45), with no underline. Twenty points of difference across
        // three channels is not a difference anyone sees.
        //
        // Founder, looking at an article that contains two real hyperlinks:
        // _"I don't see any links inside of this article: no internal links,
        // no external links, no anything like that."_ He was right, and the
        // whole content-doctrine argument sitting behind those links — that
        // naming a source without linking it is worse than not citing it —
        // was silently undone at the last render step. `cites_its_sources`
        // passed; the reader still could not check a single claim.
        //
        // The underline is the fix rather than a brighter colour: it is the
        // one link affordance that does not depend on a hue, so it survives
        // both themes and any future palette change. Restrained — a 40%
        // rule that resolves on hover — not a blue 1997 underline.
        'prose-a:text-primary prose-a:underline prose-a:decoration-primary/40 prose-a:underline-offset-2',
        'hover:prose-a:decoration-primary',
        className,
      )}
    >
      {markdownRuns ? (
        markdownRuns.map((run, index) => (
          <Fragment key={index}>
            {run.text ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={inlineMarkdownComponents}>
                {/*
                  Same streaming-prefix safety as the string path below, run
                  per text fragment since a citation array can split mid-turn.
                */}
                {completePartialMarkdown(run.text)}
              </ReactMarkdown>
            ) : null}
            {run.element}
          </Fragment>
        ))
      ) : (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {/*
            Streaming hands this a PREFIX of the answer, so it arrives mid-token
            several times a second. Closing the open token first is what stops
            `**texture` rendering its asterisks and `[report](https://exa`
            rendering as raw bracket text. Proven idempotent on settled text by
            `__tests__/complete-partial-markdown.test.ts`, so it runs
            unconditionally rather than threading a streaming flag to every call
            site.
          */}
          {completePartialMarkdown(children as string)}
        </ReactMarkdown>
      )}
    </div>
  );
}
